/* eslint-disable import/no-extraneous-dependencies */
import { message } from 'antd';
import * as auth0 from 'auth0-js';
import { createContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Observable, first, share } from 'rxjs';
import { environment } from 'src/app/constants';
import { User } from 'src/features/common-types';

interface AuthContextStore {
  user?: User;
  login: () => void;
  logout: () => void;
  isProfileLoading: boolean;
  disclaimerAccepted: boolean;
  handleAuthentication: () => void;
  setDisclaimerAccepted: React.Dispatch<React.SetStateAction<boolean>>;
}

export const AuthContext = createContext<AuthContextStore>({} as AuthContextStore);

type Authorization = { permissions: string[]; roles: string[]; groups: string[] };
type Auth0Profile = auth0.Auth0UserProfile & Record<string, { authorization: Authorization }>;

const scope = 'openid profile email';

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User>();
  const [profile, setProfile] = useState<Auth0Profile>();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [isProfileLoading, setIsProfileLoading] = useState(false);
  const [userPermissions, setUserPermissions] = useState<string[]>([]);
  const [accessToken, setAccessToken] = useState<string>();
  const [idToken, setIdToken] = useState<string>();
  const [scopes, setScopes] = useState<string>();
  const [expiresAt, setExpiresAt] = useState(0);
  const [authorization, setAuthorization] = useState<Authorization>();

  const navigate = useNavigate();

  const auth0Client = new auth0.WebAuth({
    clientID: environment.clientID,
    domain: environment.domain,
    responseType: 'token id_token',
    redirectUri: environment.callbackURL,
    audience: environment.audience,
    scope,
  });

  const resetLocalStorage = () => {
    setUser(undefined);

    localStorage.removeItem('id_token');
    localStorage.removeItem('access_token');
    localStorage.removeItem('expires_at');
    localStorage.removeItem('scopes');
    localStorage.removeItem('disclaimerAccepted');
    localStorage.removeItem('roles');
    localStorage.removeItem('groups');
    localStorage.removeItem('permissions');
    localStorage.removeItem('password-expiry');
    localStorage.removeItem('userId');
  };

  const getUserInfo = () => {
    const accessTokenVal = localStorage.getItem('access_token');
    if (accessTokenVal) {
      auth0Client.client.userInfo(accessTokenVal, (err, userProfile) => {
        if (userProfile && userProfile.email) {
          localStorage.setItem('userId', userProfile.email);
          setUser({
            name: userProfile.name,
            email: userProfile.email,
            id: userProfile.sub,
          });
          setProfile(userProfile as Auth0Profile);
        }
      });
    }
  };

  const getAuth0UserProfile = () => {
    return new Observable<Auth0Profile>((observer) => {
      if (!profile && !isProfileLoading && accessToken) {
        setIsProfileLoading(true);

        // send the request to auth0
        auth0Client.client.userInfo(accessToken, (_err, userProfile) => {
          if (userProfile) {
            // set global profile
            setProfile(userProfile as Auth0Profile);

            // get user permissions from auth0 profile
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const userMeta = (userProfile as Auth0Profile)[environment.namespace + 'user_metadata']
              ? ((userProfile as Auth0Profile)[environment.namespace + 'user_metadata'] as {
                  authorization: { permissions: string[] };
                })
              : null;
            if (userMeta && userMeta.authorization && userMeta.authorization.permissions) {
              setUserPermissions(userMeta.authorization.permissions);
            }

            setIsProfileLoading(false);

            setUser({
              id: userProfile.sub,
              name: userProfile.name,
              email: userProfile.email ?? '',
            });

            return observer.next(profile);
          } else {
            setTimeout(() => {
              throw new Error('Not authenticated');
            });
          }
        });
      } else if (profile) {
        // profile exists --> send back
        return observer.next(profile);
      }
    }).pipe(first(), share());
  };

  const getProfile = (token?: string) => {
    if (!token) {
      // this error will always be thrown once
      throw new Error('Access Token must exist to fetch profile');
    }
    return new Observable<auth0.Auth0UserProfile>((observer) => {
      if (profile) {
        return observer.next(profile);
      }

      getAuth0UserProfile().subscribe((userProfile) => {
        return observer.next(userProfile);
      });
    }).pipe(first(), share());
  };

  const localLogin = (authResult: auth0.Auth0DecodedHash): void => {
    // Set the time that the access token will expire at
    const scopesArr = authResult.scope || scope || '';
    setAccessToken(authResult.accessToken);
    localStorage.setItem('access_token', authResult.accessToken || '');
    setIdToken(authResult.idToken);
    localStorage.setItem('id_token', authResult.idToken || '');
    const expiresAtVal = (authResult.expiresIn || 0) * 1000 + new Date().getTime();
    setExpiresAt(expiresAtVal);
    localStorage.setItem('expires_at', expiresAtVal.toString());
    setScopes(JSON.stringify(scopesArr));
    localStorage.setItem('scopes', scopesArr);

    getProfile(authResult.accessToken).subscribe({
      next: (userProfile) => {
        const userMeta = (userProfile as Auth0Profile)[environment.namespace + 'user_metadata'];
        setAuthorization(userMeta.authorization);
        localStorage.setItem('authorization', JSON.stringify(userMeta.authorization));
        console.log('authorization---', authorization);
        localStorage.setItem('roles', JSON.stringify(userMeta.authorization?.roles));
        localStorage.setItem('groups', JSON.stringify(userMeta.authorization.groups));
        localStorage.setItem('permissions', JSON.stringify(userMeta.authorization.permissions));
      },
      error: (err: { error: string }) => {
        navigate('/');
        alert(`Error: ${err.error}. Check the console for further details.`);
      },
    });
  };

  const logout = () => {
    resetLocalStorage();
    setIsAuthenticated(false);
    setUser(undefined);
    auth0Client.logout({
      returnTo: environment.baseUrl,
      clientID: environment.clientID,
    });
  };

  const handleAuthentication = () => {
    auth0Client.parseHash((err, authResult) => {
      if (authResult && authResult.accessToken && authResult.idToken) {
        localLogin(authResult);
        if (!disclaimerAccepted) {
          navigate('/disclaimer');
        } else {
          navigate('/dashboard');
        }
      } else if (err) {
        if (err.errorDescription && err.errorDescription === 'password-expiry') {
          logout();
          localStorage.setItem('password-expiry', err.errorDescription);
        } else {
          logout();
          alert(`Error: ${err.error}. Check the console for further details.`);
        }
        setIsProfileLoading(false);
        navigate('/');
      }
    });
  };

  const login = () => {
    resetLocalStorage();
    auth0Client.authorize();
  };

  const checkSession = () => {
    const strExpiresAt = localStorage.getItem('expires_at');

    if (!strExpiresAt) return;

    const expiresAtVal = JSON.parse(strExpiresAt) as number;
    if (new Date().getTime() < expiresAtVal) {
      setIsAuthenticated(true);
      getUserInfo();
    } else {
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    checkSession();
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        isProfileLoading,
        setDisclaimerAccepted,
        disclaimerAccepted,
        handleAuthentication,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthProvider;
